import { useAuth0 } from "@auth0/auth0-react";
import { useSessionStorageValue, useUpdateEffect } from "@react-hookz/web";
import { useAtom } from "jotai";
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { OnboardingStepThankYou } from "~/components/onboarding/onboarding-step-thank-you";
import {
  useSetInfoAsUpToDateMutation,
  useUpdateSellerMutation,
} from "~/graphql/graphql.gen";
import type { BrandMatch } from "~/routes/root";
import { brandState } from "~/stores/brand";
import { getCorrectStep } from "~/utils/utils";

import { Stepper } from "../stepper/Stepper";
import { XChooseHeader } from "../x-choose-header/x-choose-header";
import type { OnBoardingContactFormProps } from "./onboarding-contact-form";
import { OnBoardingContactForm } from "./onboarding-contact-form";
import { OnboardingStepAuth } from "./onboarding-step-auth/onboarding-step-auth";
import type { OnboardingStepBillingInfoProps } from "./onboarding-step-billing-info";
import { OnboardingStepBillingInfo } from "./onboarding-step-billing-info";
import type { OnboardingStepCommitmentsProps } from "./onboarding-step-commitments";
import { OnboardingStepCommitments } from "./onboarding-step-commitments";
import type { OnboardingStepReturnAddressProps } from "./onboarding-step-return-address";
import { OnboardingStepReturnAddress } from "./onboarding-step-return-address";
import type { OnboardingStepShippingProps } from "./onboarding-step-shipping";
import { OnboardingStepShipping } from "./onboarding-step-shipping";
import { OnboardingStepWelcome } from "./onboarding-step-welcome";

export function OnboardingSteps() {
  const [brand, setBrand] = useAtom(brandState);
  const { user } = useAuth0();
  const isInternalUser = user?.["https://appchoose.io/claims/isInternalUser"];

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryParams = new URLSearchParams(location.search);
  const { brandId = "", step } = useParams<BrandMatch>();
  const [success, setSuccess] = useState(false);
  const { set: setInviteCode } = useSessionStorageValue<string>("inviteCode");

  const { mutateAsync: updateSellerMutation } = useUpdateSellerMutation();
  const { mutateAsync: setInfoAsUpToDateMutation } =
    useSetInfoAsUpToDateMutation();

  const steps = [
    {
      label: t("onboarding.welcome.name"),
      component: OnboardingStepWelcome,
    },
    {
      label: t("onboarding.signup.name"),
      component: OnboardingStepAuth,
    },
    {
      label: t("onboarding.shipping.name"),
      component: OnboardingStepShipping,
    },
    {
      label: t("onboarding.contact.name"),
      component: OnBoardingContactForm,
    },
    {
      label: t("onboarding.return_address.name"),
      component: OnboardingStepReturnAddress,
    },
    {
      label: t("onboarding.billing.name"),
      component: OnboardingStepBillingInfo,
    },
    {
      label: t("onboarding.commitments.name"),
      component: OnboardingStepCommitments,
    },
  ];

  const [sanitizedStep, setSanitizedStep] = useState<number>(
    getCorrectStep(step, steps.length - 1),
  );

  useEffect(() => {
    if (queryParams.has("invite-code")) {
      setInviteCode(queryParams.get("invite-code") as string);
    }
  }, []);

  useUpdateEffect(() => {
    setSanitizedStep(getCorrectStep(step, steps.length - 1));
  }, [step]);

  if (brand?.accept_contract === true) {
    if (!queryParams.has("forceOnboardingChasse") || !isInternalUser) {
      return <Navigate to={`/${brandId}/home`} />;
    }
  }

  const goToNextStep = () => {
    if (sanitizedStep === steps.length - 1) {
      onEndOnboarding();
    } else {
      navigate(`/${brandId}/onboarding/${sanitizedStep + 1}${location.search}`);
      setSanitizedStep(sanitizedStep + 1);
    }
  };

  const onEndOnboarding = async () => {
    if (!brand) return;
    setBrand({
      ...brand,
      accept_contract: true,
      need_refresh_info: false,
    });

    await updateSellerMutation({
      updateSeller: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        accept_contract: true,
      },
    });
    await setInfoAsUpToDateMutation({});

    setSuccess(true);
  };

  const Step = steps[sanitizedStep]?.component as React.ElementType<
    | OnboardingStepShippingProps
    | OnBoardingContactFormProps
    | OnboardingStepReturnAddressProps
    | OnboardingStepBillingInfoProps
    | OnboardingStepCommitmentsProps
  >;

  return success ? (
    <OnboardingStepThankYou />
  ) : (
    <div className="onboarding mx-auto w-full px-4 py-10 sm:max-w-4.5xl sm:px-6">
      <XChooseHeader brandName={brand?.name ?? ""}>
        <Stepper
          steps={steps}
          currentStep={sanitizedStep}
          className="w-full sm:max-w-[22.5rem]"
        />
      </XChooseHeader>
      <main className="sm:px-[4.75rem]">
        <Routes>
          <Route
            path={":step?/:substep?"}
            element={<Step goToNextStep={goToNextStep} />}
          />
        </Routes>
      </main>
    </div>
  );
}
